import { useContext, useEffect, useRef, useState } from "react";
import { DialogActions, Button } from "@mui/material";
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  Scrolling,
  Item,
  Paging,
  Toolbar,
} from "devextreme-react/data-grid";
import { calculateGridHeight, convertUTCTimeToLocal, downloadExcelFile } from "../../util/Utils";
import { LoadExcelFileModalProps } from "./BulkSiteCreation.types";
import { TextBox } from "devextreme-react";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { hubConnection } from "../../signalr/signalr-connection";
import { signalRMethods } from "../../constants";
import { CloudDownloadOutlined } from "@mui/icons-material";

export const LoadExcelFileModal = ({
  setBulkSiteMainPage,
}: LoadExcelFileModalProps) => {
  const { user } = useContext(OidcIdentityContext);
  const userEmail = user?.profile.preferred_username!;
  const initialStateData = { pending: true, records: [], totalCount: 0 };
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const { showSnackbar } = useSnackbar();
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationList, setValidationList] = useState(initialStateData);
  const columns = [
    { field: "created", caption: "Created Date", minWidth: 170 },
    { field: "createdBy", caption: "CreatedBy", minWidth: 100 },
    { field: "lastModified", caption: "LastModified Date", minWidth: 170 },
    { field: "lastModifiedBy", caption: "LastModifiedBy", minWidth: 130 },
    { field: "fileName", caption: "FileName", minWidth: 100 },
    { field: "totalRecords", caption: "TotalRecords", minWidth: 130 },
    { field: "numberOfValidRecords", caption: "Number Valid Records Matched", minWidth: 230 },
    { field: "numberOfRejectedRecords", caption: "Number Records Rejected", minWidth: 200 },
    { field: "fileWithRejectedRecords", caption: "File With Records Rejected", minWidth: 200 },
  ];

  const refreshButtonOptions = {
    icon: "refresh",
    onClick: () => {
      fetchData();
    },
  };

  const fetchData = async () => {
    setValidationList({ pending: true, records: [], totalCount: 0 });
    try {
      const result =
        await createSitesClientApi.taxPortal_GetFileProcessValidationGrid();
      const updatedResult = result?.data.map((row: any) => {
              return {
                ...row,
                created: convertUTCTimeToLocal(row.created),
                lastModified: convertUTCTimeToLocal(row.lastModified)
              }
      })
      setValidationList({
        pending: false,
        records: updatedResult,
        totalCount: result?.data.length,
      });
    } catch (error) {
      setValidationList({ pending: false, records: [], totalCount: 0 });
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleDownloadTemplate = async () => {
     try {
      const response: any =
        await createSitesClientApi.taxportal_DownloadTemplate();
      if (response) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a');
        a.href = url
        a.download = 'SiteCreationExcelTemplate.xlsx'
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  }

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];
    if (
      (selectedFile && selectedFile?.type === "application/vnd.ms-excel") ||
      selectedFile?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError("");
      event.target.value = null;
    } else {
      setFile(null);
      setFileName("");
      setError("Please select a valid Excel file.");
    }
  };

  const handleLoadFile = async () => {
    if (!file) {
      setError("Please select a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const apiResults = await createSitesClientApi.taxPortal_UploadExcel(
        formData,
        userEmail
      );
      setFile(null);
      setFileName("");
      if (apiResults) {
        showSnackbar({ message: SnackbarMessage.VALIDATION_IS_IN_PROGRESS });
        fetchData();
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  const handleCancel = () => {
    setBulkSiteMainPage(true);
  };

  const handleDownloadRejectedRecordsFile = async (
    id: string,
    fileName: string
  ) => {
    try {
      const base64String =
        await createSitesClientApi.taxportal_DownloadFileWithRejectedRecords(id);
      if (base64String) {
        downloadExcelFile(base64String, fileName || "RejectedRecords.xlsx");
      }
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleSignalRMessage = (message: string) => {
      if (message && message.includes("records rejected")) {
        fetchData();
      }
    };
    hubConnection.on(signalRMethods.success, handleSignalRMessage);

    return () => {
      hubConnection.off(signalRMethods.success, handleSignalRMessage);
    };
  }, [hubConnection]);

  const FileButton = () => (
    <div style={{ position: "relative" }}>
      <TextBox
        value={fileName}
        placeholder="No File Selected"
        width={320}
        buttons={[
          {
            name: "fileButton",
            location: "after",
            options: {
              stylingMode: "contained",
              text: "Choose File",
              icon: "file",
              elementAttr: {
                class: "choose-file-button",
              },
              onClick: () => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              },
            },
          },
        ]}
      />
      <input
        type="file"
        accept=".xls,.xlsx"
        style={{ display: "none" }}
        id="file-upload"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </div>
  );

  return (
    <div style={{ marginRight: "20px" }}>
      <DataGrid
        id="grid-bulksite"
        keyExpr="id"
        showBorders
        allowColumnResizing
        dataSource={validationList.records || []}
        height={calculateGridHeight(260)}
        remoteOperations
        noDataText={validationList.pending ? "Loading..." : "No Data"}
      >
        <Scrolling columnRenderingMode="virtual" />
        <FilterRow visible />
        <HeaderFilter visible />
        <Paging enabled={false} />
        <Toolbar>
          <Item location="after" render={FileButton} />
          <Item>
            <Button
              variant="outlined"
              disabled={!file}
              onClick={handleLoadFile}
            >
              Upload File
            </Button>
          </Item>
          <Item>
            <Button
              component="label"
              variant="contained"
              color="success"
              onClick={handleDownloadTemplate}
              startIcon={<CloudDownloadOutlined />}
            >
              Download Template
            </Button>
          </Item>
          <Item
            widget="dxButton"
            options={refreshButtonOptions}
            name="alignRight"
          />
        </Toolbar>

        {columns.map((col: any) =>
          col.field === "fileWithRejectedRecords" ? (
            <Column
              key={col.field}
              dataField={col.field}
              caption={col.caption}
              minWidth={col.minWidth}
              alignment="left"
              cellRender={({ data }) => {
                return data.fileWithRejectedRecords ? (
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadRejectedRecordsFile(
                        data.id,
                        data.fileWithRejectedRecords
                      );
                    }}
                    href={data.fileWithRejectedRecords}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {data.fileWithRejectedRecords}
                  </a>
                ) : null;
              }}
            />
          ) : (
            <Column
              key={col.field}
              dataField={col.field}
              caption={col.caption}
              minWidth={col.minWidth}
              alignment="left"
            />
          )
        )}
      </DataGrid>
      <div className="grid-pagination">
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Back
          </Button>
        </DialogActions>
      </div>
    </div>
  );
};
